// FILE: src/components/ContactForm.jsx
// Purpose: Add/Edit contact form used inside modals.
// Changes you asked for:
// - Placeholders instead of labels
// - Modern select UI (shared .select-field wrapper + caret)
// - Prevent autofocus when the modal opens (blur on mount)
// - Required fields enforcement kept simple: at least a name/email/phone

import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  addDoc,
  collection,
  doc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";

export default function ContactForm({
  user,
  contact = null,                // if provided -> edit mode
  onAdded,
  onUpdated,
  onCancel,
  categories = ["Family", "Friends", "Advisors", "Medical", "Legal", "Work", "Other"],
}) {
  const isEdit = !!(contact && contact.id);

  const [firstName, setFirstName] = useState(contact?.firstName || "");
  const [lastName, setLastName] = useState(contact?.lastName || "");
  const [email, setEmail] = useState(contact?.email || "");
  const [phone, setPhone] = useState(contact?.phone || "");
  const [category, setCategory] = useState(
    (categories.includes(contact?.relationship) && contact?.relationship) || categories[0] || "Family"
  );
  const [address, setAddress] = useState(contact?.address || "");
  const [notes, setNotes] = useState(contact?.notes || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Prevent any field from auto-focusing (avoid mobile keyboard popping up)
  useEffect(() => {
    const t = setTimeout(() => {
      try {
       if (typeof document !== "undefined") {
          const active = document.activeElement;
          if (active && typeof active.blur === "function") {
            active.blur();
          }
        }
      } catch {}
    }, 0);
    return () => clearTimeout(t);
  }, []);

  function buildName() {
    const n = [firstName, lastName].filter(Boolean).join(" ").trim();
    return n || email || phone || "";
  }

  async function handleSave() {
    if (!user?.uid) {
      setErr("Not signed in.");
      return;
    }
    const name = buildName();
    if (!name) {
      setErr("Please enter at least a name, email, or phone.");
      return;
    }

    setBusy(true);
    setErr("");

    try {
      if (isEdit) {
        await updateDoc(doc(db, "contacts", contact.id), {
          uid: contact.uid || user.uid,
          name,
          firstName,
          lastName,
          email,
          phone,
          relationship: category,
          address,
          notes,
          updatedAt: serverTimestamp(),
        });
        onUpdated && onUpdated();
      } else {
        await addDoc(collection(db, "contacts"), {
          uid: user.uid,
          name,
          firstName,
          lastName,
          email,
          phone,
          relationship: category,
          address,
          notes,
          createdAt: serverTimestamp(),
        });
        onAdded && onAdded();
      }
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="contacts-form">
      <div className="grid2">
        <div>
          <input
            className="input"
            placeholder="First name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            // no autoFocus
          />
        </div>
        <div>
          <input
            className="input"
            placeholder="Last name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </div>
      </div>

      <div className="grid2">
        <div>
          <input
            className="input"
            type="email"
            placeholder="Email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div>
          <input
            className="input"
            type="tel"
            placeholder="Phone"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>
      </div>

      {/* Modern select */}
      <div className="select-field" style={{ marginTop: 8 }}>
        <select value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Category">
  
          {categories.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <span className="select-field__caret" aria-hidden="true">▾</span>
      </div>

      <div style={{ marginTop: 8 }}>
        <input
          className="input"
          placeholder="Address (optional)"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
        />
      </div>

      <div style={{ marginTop: 8 }}>
        <textarea
          className="input"
          placeholder="Notes (optional)"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          style={{ resize: "vertical" }}
        />
      </div>

      {err && (
        <div className="empty-state" role="alert" style={{ marginTop: 10 }}>
          {err}
        </div>
      )}

      <div className="form-actions" style={{ marginTop: 12 }}>
        <button type="button" className="btn btn-main" disabled={busy} onClick={handleSave}>
          {isEdit ? "Save" : "Create"}
        </button>
        <button type="button" className="btn btn-cancel" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
